import React, {
    useState, useCallback, useRef,
} from 'react'
import {
    DefaultButton, MessageBar, MessageBarType,
} from '@fluentui/react'
import useTranslate from 'helpers/hooks/useTranslate'
import CompaniesHandler from 'requests/handlers/companiesHandler'
import VehicleEquipment from 'requests/objects/vehicleEquipment'
import Card from 'components/containers/card'
import styles from 'styles/components/pages/companies/accessibilities/pivot-accessibilities.module.scss'
import VehicleEquipmentList from 'components/pages/company/vehicle-equipment/vehicle-equipment-list'
import VehicleEquipmentModal from 'components/pages/company/vehicle-equipment/vehicle-equipment-modal'

/**
 * Pivot vehicle equipments
 * @param {object} props Props
 * @param {string} props.lang Lang
 * @param {CompaniesHandler} props.companiesHandler companiesHandler
 * @param {number=} props.companyId companyId
 * @param {VehicleEquipment[]=} props.vehicleEquipments vehicleEquipments
 * @param {(vehicleEquipment: VehicleEquipment, isDeletion?: boolean) => void} props.onChange onChange
 * @param {function(import('redux/slices/common').PayloadModal):void} props.setModal Set Modal
 * @returns {JSX.Element} Element
 */
export default function PivotVehicleEquipments({
    lang,
    companiesHandler,
    companyId,
    setModal,
    vehicleEquipments,
    onChange,
}) {
    const { tKey, tObj } = useTranslate({ lang })
    const [errorMessage, setErrorMessage] = useState(null)
    const [isVehicleEquipmentModalVisible, setIsVehicleEquipmentModalVisible] = useState(false)
    const [vehicleEquipment, setVehicleEquipment] = useState(new VehicleEquipment())

    /** @type {React.MutableRefObject<import('requests/apiHandler').RequestApi<VehicleEquipment>>} */
    const deleteVehicleEquipmentHandler = useRef(null)

    const editVehicleEquipment = useCallback(
        /**
         * Edit vehicle equipment
         * @param {VehicleEquipment} vehicleEquipmentToEdit vehicleEquipmentToEdit
         */
        vehicleEquipmentToEdit => {
            setVehicleEquipment(vehicleEquipmentToEdit)
            setIsVehicleEquipmentModalVisible(true)
        }, [],
    )

    const deleteVehicleEquipment = useCallback(
        /**
         * Delete vehicle equipment
         * @param {VehicleEquipment} vehicleEquipmentToDelete vehicleEquipmentToDelete
         */
        vehicleEquipmentToDelete => {
            setModal({
                show: true,
                title: tKey('removeVehicleEquipment'),
                subTitle: tKey('sureRemoveVehicleEquipment'),
                callback: async () => {
                    try {
                        deleteVehicleEquipmentHandler.current = companiesHandler.removeVehicleEquipmentById(
                            companyId,
                            vehicleEquipmentToDelete.vehicleEquipmentId,
                        )
                        const vehicleEquipmentDeleted = await deleteVehicleEquipmentHandler.current.fetch()
                        setErrorMessage(null)
                        onChange(vehicleEquipmentDeleted, true)
                    } catch (error) {
                        setErrorMessage(error ?? 'anErrorHasOccurred')

                        // eslint-disable-next-line no-console
                        console.log(error)
                    }
                },
            })
        }, [companiesHandler, companyId, onChange, setModal, tKey],
    )

    const addVehicleEquipment = useCallback(() => {
        setVehicleEquipment(new VehicleEquipment({ companyId }))
        setIsVehicleEquipmentModalVisible(true)
    }, [companyId])

    return (
        <main className={styles['pivot-accessibilities']}>
            <Card
                iconName="PageData"
                title={tKey('vehicleEquipment')}
                headerComponent={(
                    <DefaultButton
                        onClick={e => {
                            e.preventDefault()
                            addVehicleEquipment()
                        }}
                        text={tKey('add')}
                    />
                )}
                hasDividerMargin={false}
            >
                {errorMessage
                    && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            truncated
                            onDismiss={() => setErrorMessage(null)}
                        >
                            {tObj(errorMessage)}
                        </MessageBar>
                    )}
                <VehicleEquipmentList
                    editVehicleEquipment={editVehicleEquipment}
                    deleteVehicleEquipment={deleteVehicleEquipment}
                    items={vehicleEquipments}
                    lang={lang}
                />
            </Card>
            <VehicleEquipmentModal
                vehicleEquipmentData={vehicleEquipment}
                isVisible={isVehicleEquipmentModalVisible}
                companiesHandler={companiesHandler}
                onChange={vehicleEquipmentReturned => {
                    if (vehicleEquipmentReturned)
                        onChange(vehicleEquipmentReturned)
                    setIsVehicleEquipmentModalVisible(false)
                }}
                lang={lang}
            />
        </main>
    )
}
